import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
	Typography,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Breadcrumbs,
	Link,
	Grid,
	makeStyles,
	TablePagination,
	TableSortLabel,
	IconButton
} from '@material-ui/core';
import CloseIcon from "@material-ui/icons/Close";
import instance from "api/globalaxios";
import { displayEmail, getCaseReferenceNumber, getCaseStatusLabel, isCaseReopened, safeDateFormat } from "../utils";
import SearchCaseForm from "../Components/SearchCase";
import JudgePanelSelector from "./JudgePanelSelector";
import GlobalSearch from "../Components/GlobalSearch/GlobalSearch";
import ArbitratorDashboard from "../Components/ArbitratorDashboard/ArbitratorDashboard";
import FormError from "../Components/FormError";
import Swal from "sweetalert2";

// Shared cell styles consolidated into the makeStyles configuration
const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		[theme.breakpoints.down("sm")]: {
			padding: 0
		}
	},
	tableCell: {
		borderRight: 'none',
		borderLeft: 'none',
		padding: "10px",
		fontSize: "0.875rem"
	},
	nowrapCell: {
		whiteSpace: "nowrap"
	},
	categoryCell: {
		minWidth: 160
	},
	// Phones only: the table scrolls sideways, so say so
	scrollHint: {
		display: "none",
		fontSize: "0.75rem",
		color: TEXT.muted,
		marginBottom: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			display: "block"
		}
	},
	outerHeader: {
		backgroundColor: '#eeeeee' // Light gray for outer table header
	},
	innerHeader: {
		backgroundColor: '#e0e0e0' // Different shade of light gray for inner nested table header
	},
	outerHeaderText: {
		color: TEXT.primary,
		fontWeight: 600
	},
	innerHeaderText: {
		color: TEXT.secondary,
		fontWeight: 500
	},
	// Keep the sort control visually identical to the other column headings —
	// TableSortLabel otherwise recolours itself on hover/focus/active.
	sortLabel: {
		color: 'inherit',
		fontSize: 'inherit',
		fontWeight: 'inherit',
		'&:hover': { color: 'inherit' },
		'&:focus': { color: 'inherit' },
		'&.MuiTableSortLabel-active': { color: 'inherit' },
		'& .MuiTableSortLabel-icon': {
			color: 'inherit !important',
			fontSize: '1.15em',
			marginLeft: 2,
			marginRight: 0
		}
	}
}));

// asc → desc → unsorted
const nextSortOrder = (current) => (current === "" ? "asc" : current === "asc" ? "desc" : "");

const statusColor = (status) => {
	if (!status) return {};
	const s = status.toLowerCase();
	if (s === 'new' || s === 'open') return { backgroundColor: '#e8f5e9', color: TEXT.success };
	if (s === 'closed' || s === 'resolved') return { backgroundColor: '#fce4ec', color: TEXT.error };
	return { backgroundColor: '#e3f2fd', color: TEXT.info };
};

// --- Row Component for Arbitrators List ---
const ArbitratorRow = ({ row, localClasses, onDashboardClick }) => {
	const name = [row?.user?.first_name, row?.user?.last_name].filter(Boolean).join(' ') || '—';

	return (
		<TableRow
			hover
			onClick={() => onDashboardClick(row.id)}
			style={{ cursor: 'pointer' }}
		>
			<TableCell className={localClasses.tableCell}>
				<Typography style={{ fontSize: 'inherit' }}>{name}</Typography>
			</TableCell>
			<TableCell className={localClasses.tableCell}>
				{row?.age ?? '-'}
			</TableCell>
			<TableCell className={localClasses.tableCell}>
				{row?.birth_date ?? '-'}
			</TableCell>
			<TableCell className={`${localClasses.tableCell} ${localClasses.categoryCell}`}>
				{row?.arbitrator_category_name ?? '-'}
			</TableCell>
			<TableCell className={localClasses.tableCell}>
				{row?.experience ?? '-'}
			</TableCell>
			<TableCell align="center" className={localClasses.tableCell}>
				{row?.case_count ?? 0}
			</TableCell>
		</TableRow>
	);
};

// --- Main Arbitrators Master Component ---
const Arbitrators = ({ classes }) => {
	const localClasses = useStyles();
	const [currentScreen, setCurrentScreen] = useState('list');
	const [arbitrators, setArbitrators] = useState([]);
	const [allArbitrators, setAllArbitrators] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [search, setSearch] = useState("");
	const [selectedArbitratorId, setSelectedArbitratorId] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState("all");
	const [ageSort, setAgeSort] = useState(""); // "" | "asc" | "desc"
	const [caseSort, setCaseSort] = useState(""); // "" | "asc" | "desc"
	const [stats, setStats] = useState(null);

	// Pagination configuration (fixed sizing pagination setup)
	const [page, setPage] = useState(0);
	const rowsPerPage = 10;

	// Appointment Screen States
	const [searchedCase, setSearchedCase] = useState(null);
	const [judgePanel, setJudgePanel] = useState({ presiding: '', member1: '', member2: '' });
	const [appointFormError, setAppointFormError] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const [declarationFiles, setDeclarationFiles] = useState({ presiding: null, member1: null, member2: null });

	// Fetch list of Arbitrators
	const fetchArbitrators = (searchText = search, categoryId = selectedCategory, ageOrder = ageSort, caseOrder = caseSort) => {
		let url = `/bac/masters/arbitrator/?page=${page + 1}&search=${encodeURIComponent(searchText)}&sort=true`;
		if (categoryId && categoryId !== "all") {
			url += `&category=${categoryId}`;
		}
		if (ageOrder) {
			url += `&age=${ageOrder}`;
		}
		if (caseOrder) {
			url += `&case_count=${caseOrder}`;
		}
		instance.get(url)
			.then(response => {
				const data = response.data;
				if (data && typeof data === 'object' && 'results' in data) {
					setArbitrators(data.results || []);
					setTotalCount(data.count || 0);
				} else {
					setArbitrators(Array.isArray(data) ? data : []);
					setTotalCount(Array.isArray(data) ? data.length : 0);
				}
			})
			.catch(err => console.error("Error fetching arbitrators:", err));
	};

	useEffect(() => {
		if (currentScreen === 'list') {
			fetchArbitrators(search, selectedCategory, ageSort, caseSort);
		}
	}, [currentScreen, page, search, selectedCategory, ageSort, caseSort]);

	useEffect(() => {
		if (currentScreen === 'assign') {
			instance.get('/bac/masters/arbitrator/?pagination=false&sort=true')
				.then(response => {
					const data = response.data;
					setAllArbitrators(Array.isArray(data) ? data : data.results || []);
				})
				.catch(err => console.error("Error fetching all arbitrators:", err));
		}
	}, [currentScreen]);

	useEffect(() => {
		if (currentScreen === 'list') {
			instance.get("/bac/arbitrators/stats/")
				.then(response => {
					setStats(response.data);
				})
				.catch(err => console.error("Error fetching stats:", err));
		}
	}, [currentScreen]);

	useEffect(() => {
		if (searchedCase && searchedCase.arbitrators) {
			const presidingArb = searchedCase.arbitrators.find(a => a.is_presiding);
			const nonPresidingArbs = searchedCase.arbitrators.filter(a => !a.is_presiding);

			setJudgePanel({
				presiding: presidingArb ? Number(presidingArb.id) : '',
				member1: nonPresidingArbs[0] ? Number(nonPresidingArbs[0].id) : '',
				member2: nonPresidingArbs[1] ? Number(nonPresidingArbs[1].id) : ''
			});
		} else {
			setJudgePanel({ presiding: '', member1: '', member2: '' });
		}
		
		setDeclarationFiles({ presiding: null, member1: null, member2: null });
	}, [searchedCase]);


	const handleSearchChange = (val) => {
		setSearch(val);
		setPage(0);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	// Column headers cycle ascending → descending → unsorted (the default).
	// Only one column drives the ordering, so picking one clears the other.
	const handleAgeSortToggle = () => {
		setAgeSort(nextSortOrder(ageSort));
		setCaseSort("");
		setPage(0);
	};

	const handleCaseSortToggle = () => {
		setCaseSort(nextSortOrder(caseSort));
		setAgeSort("");
		setPage(0);
	};

	const handleSearchSuccess = (caseData) => {
		setSearchedCase(caseData);
	};

	const uploadDeclaration = async (disputeId, arbId, file) => {
		const formData = new FormData();
		formData.append("file", file);
		const response = await instance.post(
			`/bac/cases/${disputeId}/arbitrators/${arbId}/declaration/`,
			formData,
			{ headers: { "Content-Type": "multipart/form-data" } }
		);
		return response.status === 200 || response.status === 201;
	};

	const handleAssignmentSubmit = async () => {
		if (!searchedCase) return;

		setAppointFormError('');

		if (searchedCase.status === 'closed') {
			setAppointFormError("Cannot appoint arbitrator(s). Case is Disposal.");
			return;
		}

		if (!judgePanel.presiding && !judgePanel.member1 && !judgePanel.member2) {
			setAppointFormError("No arbitrator is selected. Please select at least one arbitrator.");
			return;
		}

		// Every slot is optional now, so the presiding entry has to be guarded
		// too - otherwise an empty slot posts an arbitrator_id of "".
		const postArbitrators = [
			...(judgePanel.presiding ? [{ arbitrator_id: judgePanel.presiding, is_main: true, position: 1 }] : []),
			...(judgePanel.member1 ? [{ arbitrator_id: judgePanel.member1, is_main: false, position: 2 }] : []),
			...(judgePanel.member2 ? [{ arbitrator_id: judgePanel.member2, is_main: false, position: 3 }] : []),
		];

		try {
			setSubmitting(true);
			const response = await instance.post(`/bac/cases/${searchedCase.id}/arbitrators/`, {
				arbitrators: postArbitrators,
			});

			if (response.status === 200 || response.status === 201) {
				const slotToArbId = {
					presiding: judgePanel.presiding,
					member1: judgePanel.member1,
					member2: judgePanel.member2,
				};
				const failedDeclarations = [];
				for (const slot of ['presiding', 'member1', 'member2']) {
					const file = declarationFiles[slot];
					const arbId = slotToArbId[slot];
					if (file && arbId) {
						try {
							await uploadDeclaration(searchedCase.id, arbId, file);
						} catch (e) {
							console.error(`Declaration upload failed for ${slot}:`, e);
							failedDeclarations.push(slot);
						}
					}
				}

				if (failedDeclarations.length > 0) {
					Swal.fire("Partially Completed", "Arbitrator(s) appointed, but some declarations failed to upload. Please try uploading those again.", "warning");
				} else {
					Swal.fire("Success", "Arbitrator(s) successfully appointed!", "success");
				}
				setSearchedCase(null);
				setJudgePanel({ presiding: '', member1: '', member2: '' });
				setDeclarationFiles({ presiding: null, member1: null, member2: null });
				setPage(0);
				setCurrentScreen('list');
			} else {
				setAppointFormError("Failed to appoint arbitrator(s).");
			}
		} catch (err) {
			console.error("Appointment error:", err);
		} finally {
			setSubmitting(false);
		}
	};

	const refNo = getCaseReferenceNumber(searchedCase);

	return (
		<Box className={localClasses.root}>
			{currentScreen === 'list' ? (
				/* --- SCREEN 1: LIST / TABLE VIEW --- */
				<Box>

					{/* Category Cards */}
					{stats?.category_breakdown && (
						<Box display="flex" flexWrap="wrap" mb={3} style={{ gap: '1rem' }}>
							{stats.category_breakdown.map((cat) => {
								const isSelected = selectedCategory === cat.category_id;
								return (
									<Box
										key={cat.category_id}
										onClick={() => {
											if (isSelected) {
												setSelectedCategory("all");
											} else {
												setSelectedCategory(cat.category_id);
											}
											setPage(0);
										}}
										style={{
											cursor: "pointer",
											padding: "1rem",
											border: isSelected ? "2px solid #61386d" : "1px solid #E5E5E5",
											borderRadius: "8px",
											backgroundColor: isSelected ? "#fbf9fc" : "#ffffff",
											minWidth: "160px",
											textAlign: "center",
											userSelect: "none"
										}}
									>
										<Typography style={{ fontSize: "0.75rem", fontWeight: 600, color: isSelected ? "#61386d" : "#757575", textTransform: "uppercase", letterSpacing: "0.04em" }}>
											{cat.category}
										</Typography>
										<Typography style={{ fontSize: "1.75rem", fontWeight: 700, color: isSelected ? "#61386d" : "#333333", marginTop: "8px" }}>
											{cat.arbitrator_count ?? 0}
										</Typography>
									</Box>
								);
							})}
						</Box>
					)}
					<Box display="flex" justifyContent="space-between" alignItems="stretch" mb={2} style={{ gap: '1rem' }}>
						<GlobalSearch
							value={search}
							onSearchChange={handleSearchChange}
							placeholder="Search Arbitrators..."
							style={{ width: '400px', minWidth: 0 }}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={() => setCurrentScreen('assign')}
							size="small"
							style={{ textTransform: "none", flexShrink: 0, whiteSpace: "nowrap" }}
						>
							Appoint Arbitrator(s)
						</Button>
					</Box>

					{arbitrators.length > 0 && (
						<Typography className={localClasses.scrollHint}>
							Swipe left on the table to see more columns →
						</Typography>
					)}

					<TableContainer component={Paper} style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
						{/* Style Modification 1: Removed marginTop style override completely */}
						<Table aria-label="arbitrators table" style={{ maxWidth: '100%', marginTop: 0 }}>
							<TableHead>
								<TableRow className={localClasses.outerHeader}>
									<TableCell className={`${localClasses.tableCell} ${localClasses.outerHeaderText} ${localClasses.nowrapCell}`}>Name</TableCell>
									{/* <TableCell className={`${localClasses.tableCell} ${localClasses.outerHeaderText}`}>Email</TableCell> */}
									<TableCell
										className={`${localClasses.tableCell} ${localClasses.outerHeaderText} ${localClasses.nowrapCell}`}
										sortDirection={ageSort || false}
									>
										<TableSortLabel
											className={localClasses.sortLabel}
											active={Boolean(ageSort)}
											direction={ageSort || "asc"}
											onClick={handleAgeSortToggle}
										>
											Age
										</TableSortLabel>
									</TableCell>
									<TableCell className={`${localClasses.tableCell} ${localClasses.outerHeaderText} ${localClasses.nowrapCell}`}>Date of Birth</TableCell>
									<TableCell className={`${localClasses.tableCell} ${localClasses.outerHeaderText} ${localClasses.nowrapCell} ${localClasses.categoryCell}`}>Category</TableCell>
									<TableCell className={`${localClasses.tableCell} ${localClasses.outerHeaderText} ${localClasses.nowrapCell}`}>Experience</TableCell>
									<TableCell
										align="center"
										className={`${localClasses.tableCell} ${localClasses.outerHeaderText} ${localClasses.nowrapCell}`}
										sortDirection={caseSort || false}
									>
										<TableSortLabel
											className={localClasses.sortLabel}
											active={Boolean(caseSort)}
											direction={caseSort || "asc"}
											onClick={handleCaseSortToggle}
										>
											Number of Cases
										</TableSortLabel>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{arbitrators.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center" style={{ padding: 32, color: TEXT.muted }}>
											No arbitrators available.
										</TableCell>
									</TableRow>
								) : (
									arbitrators.map((row, index) => (
										<ArbitratorRow
											key={row.id || index}
											row={row}
											localClasses={localClasses}
											onDashboardClick={(id) => {
												setSelectedArbitratorId(id);
												setCurrentScreen('dashboard');
											}}
										/>
									))
								)}
							</TableBody>
						</Table>

						{/* Pagination Implementation */}
						{totalCount > 0 && (
							<TablePagination
								rowsPerPageOptions={[10]}
								component="div"
								count={totalCount}
								rowsPerPage={rowsPerPage}
								page={page}
								onChangePage={handleChangePage}
								onChangeRowsPerPage={() => { }}
							/>
						)}
					</TableContainer>
				</Box>
			) : currentScreen === 'dashboard' ? (
				<ArbitratorDashboard
					arbitratorId={selectedArbitratorId}
					onBack={() => setCurrentScreen('list')}
				/>
			) : (
				/* --- SCREEN 2: APPOINT ARBITRATOR(S) VIEW --- */
				<Box>
					<Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: 16 }}>
						<Link
							color="inherit"
							href="#"
							onClick={(e) => { e.preventDefault(); setCurrentScreen('list'); }}
						>
							Arbitrators
						</Link>
						<Typography color="textPrimary">Appoint Arbitrator(s)</Typography>
					</Breadcrumbs>

					<Box mb={3}>
						<SearchCaseForm
							classes={classes}
							handleSearchSuccess={handleSearchSuccess}
						/>
					</Box>

					{searchedCase && (
						<Paper style={{ padding: 24, marginTop: 16 }}>
							<Typography variant="h6" gutterBottom={!refNo}>{searchedCase.tiac_custom_dispute_id}</Typography>
							{refNo && (
								<Typography variant="body2" color="textSecondary" gutterBottom style={{ overflowWrap: "anywhere" }}>
									{refNo.label}: {refNo.value}
								</Typography>
							)}

							<Grid container spacing={3} style={{ marginBottom: 24 }}>
								<Grid item xs={12} sm={4}>
									<Typography variant="subtitle2" color="textSecondary">Claimant Name</Typography>
									<Typography variant="body1"><strong>{searchedCase.claimant?.name || 'N/A'}</strong></Typography>
									<Typography variant="caption" color="textSecondary">{displayEmail(searchedCase.claimant?.email)}</Typography>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography variant="subtitle2" color="textSecondary">Respondent Name</Typography>
									<Typography variant="body1"><strong>{searchedCase.respondent?.name || 'N/A'}</strong></Typography>
									<Typography variant="caption" color="textSecondary">{displayEmail(searchedCase.respondent?.email)}</Typography>
								</Grid>
								<Grid item xs={12} sm={4}>
									<Typography variant="subtitle2" color="textSecondary">Case Status</Typography>
									<Typography variant="body1">
										<strong style={{ color: searchedCase.status === 'closed' ? '#c62828' : isCaseReopened(searchedCase) ? TEXT.brand : '#2e7d32' }}>
											{getCaseStatusLabel(searchedCase) || 'N/A'}
										</strong>
									</Typography>
									{isCaseReopened(searchedCase) && searchedCase.reopen_date && (
										<Typography variant="caption" color="textSecondary">
											Reopened on {safeDateFormat(searchedCase.reopen_date)}
										</Typography>
									)}
								</Grid>
							</Grid>

							{searchedCase.status === 'closed' ? (
								<Box
									p={2}
									style={{
										backgroundColor: "#fde8e8",
										border: "1px solid #f8b4b4",
										// borderRadius: "8px",
										color: TEXT.error,
										fontWeight: 600,
										fontSize: "0.9rem",
										padding: "12px",
									}}
								>
									<Typography>This case is Disposal</Typography>
									{searchedCase.reason_for_closure && (
										<Typography style={{ fontWeight: 400, fontSize: "0.9rem", marginTop: "4px" }}>
											Nature of Disposal: <strong>{searchedCase.reason_for_closure}</strong>
										</Typography>
									)}
								</Box>
							) : (
								<>
									<Box
										mb={3}
										p={2}
										pl={4}
										style={{
											border: "1px solid #E5E5E5",
											borderRadius: "4px",
											backgroundColor: "#fafafa",
											textAlign: "left"
										}}
									>
										<Typography
											variant="subtitle2"
											style={{
												fontWeight: 600,
												color: TEXT.primary,
												marginBottom: 8,
												textTransform: "none"
											}}
										>
											Appointed Arbitrator(s)
										</Typography>
										{searchedCase.arbitrators && searchedCase.arbitrators.length > 0 ? (
											searchedCase.arbitrators.map((arb) => (
												<Box
													key={arb.id}
													display="flex"
													alignItems="center"
													flexWrap="wrap"
													style={{ marginBottom: 4, gap: 8 }}
												>
													<Typography
														variant="body2"
														style={{ fontSize: "0.875rem", color: TEXT.secondary }}
													>
														• {arb.name} {arb.is_presiding ? <strong>(Presiding)</strong> : ""}
													</Typography>
													{arb.declaration_uploaded && (
														<Typography
															variant="caption"
															title={arb.declaration_file_name || ""}
															style={{ color: TEXT.success, fontWeight: 600 }}
														>
															✓ Declaration uploaded
														</Typography>
													)}
												</Box>
											))
										) : (
											<Typography
												variant="body2"
												style={{
													fontSize: "0.875rem",
													color: TEXT.muted,
													fontStyle: "italic",
												}}
											>
												No arbitrators appointed yet.
											</Typography>
										)}
									</Box>

									<Box mb={3}>
										<JudgePanelSelector
											arbitrators={allArbitrators}
											value={judgePanel}
											onChange={(next) => {
												setJudgePanel(next);
												if (next.presiding || next.member1 || next.member2) {
													setAppointFormError('');
												}
											}}
										/>
									</Box>

									{/* Attach a declaration per selected arbitrator — uploaded
									    together with the appointment on Submit. */}
									{['presiding', 'member1', 'member2'].some((s) => judgePanel[s]) && (
										<Box mb={3}>
											<Typography style={{ marginBottom: 8 }}>
												Arbitrator Declaration(s) (Optional)
											</Typography>
											{[
												{ slot: 'presiding', fallback: 'Presiding/Sole Arbitrator' },
												{ slot: 'member1', fallback: 'Arbitrator 2' },
												{ slot: 'member2', fallback: 'Arbitrator 3' },
											]
												.filter(({ slot }) => judgePanel[slot])
												.map(({ slot, fallback }) => {
													const arb = allArbitrators.find(
														(a) => Number(a.id) === Number(judgePanel[slot])
													);
													const arbName = arb
														? `${arb?.user?.first_name || ''} ${arb?.user?.last_name || ''}`.trim() || fallback
														: fallback;
													const file = declarationFiles[slot];
													return (
														<Box
															key={slot}
															display="flex"
															alignItems="center"
															justifyContent="space-between"
															flexWrap="wrap"
															style={{ gap: 8, marginBottom: 6 }}
														>
															<Typography variant="body2" style={{ fontSize: "0.875rem", color: TEXT.secondary }}>
																• {arbName}
															</Typography>
															<Box display="flex" alignItems="center" style={{ gap: 8 }}>
																<input
																	id={`decl-file-${slot}`}
																	type="file"
																	accept=".pdf,.doc,.docx,image/*"
																	style={{ display: "none" }}
																	onChange={(e) => {
																		const f = e.target.files && e.target.files[0];
																		setDeclarationFiles((prev) => ({ ...prev, [slot]: f || null }));
																		e.target.value = "";
																	}}
																/>
																{file ? (
																	<>
																		<Typography variant="caption" style={{ color: TEXT.success, minWidth: 0, wordBreak: "break-all" }} title={file.name}>
																			{file.name}
																		</Typography>
																		<IconButton
																			size="small"
																			aria-label={`Remove declaration for ${arbName}`}
																			style={{ flexShrink: 0 }}
																			title="Remove file"
																			onClick={() => {
																				setDeclarationFiles((prev) => ({ ...prev, [slot]: null }));
																			}}
																		>
																			<CloseIcon fontSize="small" />
																		</IconButton>
																	</>
																) : (
																	<Button
																		variant="outlined"
																		size="small"
																		color="primary"
																		onClick={() => {
																			const el = document.getElementById(`decl-file-${slot}`);
																			if (el) el.click();
																		}}
																	>
																		Upload Declaration
																	</Button>
																)}
															</Box>
														</Box>
													);
												})}
										</Box>
									)}

									<Button
										variant="contained"
										color="primary"
										onClick={handleAssignmentSubmit}
										disabled={submitting}
									>
										{submitting ? "Submitting..." : "Submit"}
									</Button>
									<FormError message={appointFormError} />
								</>
							)}
						</Paper>
					)}
				</Box>
			)}
		</Box>
	);
};

export default Arbitrators;